import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Swords, Bot, User, ShieldAlert, Crown, Loader2, Crosshair, ShieldCheck, X, Zap, Users } from "lucide-react";
import { spaceApi } from "./spaceApi";
import { SpaceHomeBtn } from "./SpaceNav";

const DIFF_COLOR = { Easy: "#37e0a0", Normal: "#00d5ff", Hard: "#f5a623", Lethal: "#ff3b4e" };
const BK = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (p) => (p ? (p.startsWith("http") ? p : BK + p) : null);

export default function WorldMap({ onBack, onDeploy }) {
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [march, setMarch] = useState(null);
  const [revenge, setRevenge] = useState([]);
  const [last, setLast] = useState(null);
  const [incoming, setIncoming] = useState([]);
  const [board, setBoard] = useState([]);
  const [mapBg, setMapBg] = useState(null);
  const [gate, setGate] = useState(null);      // pre-flight overlay: { slot, loading, data, needSquad }
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const [m, r, lb] = await Promise.all([
        spaceApi.conquestMap(),
        spaceApi.conquestRevenge().catch(() => ({ targets: [] })),
        spaceApi.conquestLeaderboard().catch(() => ({ leaderboard: [] })),
      ]);
      setMap(m); setRevenge(r.targets || []); setBoard(lb.leaderboard || []);
    } catch (e) { toast.error(e.status === 403 ? "Reach Command Centre Lv 20 first" : "Could not load the arena"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); return () => clearInterval(timer.current); }, [load]);
  useEffect(() => {
    spaceApi.gameAssets().then((a) => {
      if (a.worldmap_backdrop) {
        const BK = process.env.REACT_APP_BACKEND_URL;
        setMapBg(a.worldmap_backdrop.startsWith("http") ? a.worldmap_backdrop : BK + a.worldmap_backdrop);
      }
    }).catch(() => {});
  }, []);

  // Live March Feed — short-poll for hostile marches inbound on our node.
  useEffect(() => {
    const id = setInterval(() => { spaceApi.conquestIncoming().then((d) => setIncoming(d.incoming || [])).catch(() => {}); }, 3000);
    spaceApi.conquestIncoming().then((d) => setIncoming(d.incoming || [])).catch(() => {});
    return () => clearInterval(id);
  }, []);

  const resolve = useCallback(async (marchId) => {
    try {
      const r = await spaceApi.conquestResolve(marchId);
      if (r.pending) return;
      clearInterval(timer.current); setMarch(null); setBusy(false);
      if (r.blocked) toast.warning(r.shielded ? "Target is shielded" : "Target IMMUNE");
      else if (r.win) { toast.success(`Victory vs ${r.target_name}! +${r.loot} Titanium · +${r.tokens_awarded} tokens`); setLast({ win: true, ...r }); }
      else { toast.error(`Defeat vs ${r.target_name}. Wiped — 12h Rebuild Shield applied.`); setLast({ win: false, ...r }); }
      await load();
    } catch { clearInterval(timer.current); setMarch(null); setBusy(false); toast.error("March resolution failed"); }
  }, [load]);

  const launchMarch = async (slot) => {
    setBusy(true);
    try {
      const r = await spaceApi.conquestAttack(slot);
      if (r.blocked) {
        if (r.need_squad) { toast.warning("Deploy a squad before you march"); setBusy(false); if (onDeploy) onDeploy(); return; }
        toast.warning(r.immune ? "Target IMMUNE (below Lv 20)" : r.allied ? "Allied — cannot raid" : "Target shielded — cannot raid");
        setBusy(false); return;
      }
      let remaining = r.duration;
      setMarch({ march_id: r.march_id, slot, remaining, target: r.target_name });
      timer.current = setInterval(() => {
        remaining -= 1;
        setMarch((mm) => (mm ? { ...mm, remaining: Math.max(0, remaining) } : mm));
        if (remaining <= 0) { clearInterval(timer.current); resolve(r.march_id); }
      }, 1000);
    } catch (e) { toast.error(e.message || "Attack failed"); setBusy(false); }
  };

  // Pre-flight gate — intercept a node attack with a squad-vs-sector confirmation.
  const openGate = async (slot, isSelf, immune, shielded) => {
    if (busy || march || isSelf || immune || shielded) return;
    setGate({ slot, loading: true, data: null, needSquad: false });
    try {
      const d = await spaceApi.conquestPreflight(slot);
      if (d.need_squad) { setGate({ slot, loading: false, data: null, needSquad: true }); return; }
      setGate({ slot, loading: false, data: d, needSquad: false });
    } catch (e) { setGate(null); toast.error(e.message || "Could not scout target"); }
  };

  const confirmGate = async () => {
    const slot = gate?.slot;
    setGate(null);
    if (slot != null) await launchMarch(slot);
  };

  return (
    <div className="ss-viewport" data-testid="space-supremacy-app">
      <div className="ss-atmosphere" /><div className="ss-grid" />
      {mapBg && <div className="wm-territory" style={{ backgroundImage: `url(${mapBg})` }} data-testid="worldmap-backdrop" />}
      <SpaceHomeBtn onClick={onBack} testid="conquest-back" />

      <div className="wm-wrap" data-testid="conquest-map">
        <p className="ss-eyebrow">Planet · PvP Conquest</p>
        <h1 className="wm-title"><Swords size={20} /> Conquest Matrix</h1>

        {loading || !map ? (
          <div className="bg-loading"><Loader2 size={26} className="bg-spin" /><span>Matchmaking region…</span></div>
        ) : (
          <>
            <div className="wm-power" data-testid="conquest-my-power">Region {map.region} · Your Power <b>{map.my_power.toLocaleString()}</b></div>

            {incoming.length > 0 && (
              <div className="wm-incoming" data-testid="conquest-incoming">
                ⚠ INBOUND HOSTILE {incoming.length > 1 ? `×${incoming.length}` : ""} — ETA {Math.min(...incoming.map((i) => i.eta))}s
              </div>
            )}

            {revenge.length > 0 && (
              <div className="wm-revenge" data-testid="conquest-revenge">
                <span className="wm-rev-t"><Crosshair size={12} /> Revenge Registry</span>
                {revenge.slice(0, 3).map((t, i) => (
                  <button key={i} className="wm-rev-btn" data-testid={`revenge-target-${i}`} onClick={() => openGate(t.enemy_slot, false, false, false)}>
                    Counter-strike {t.enemy_name}
                  </button>
                ))}
              </div>
            )}

            <div className="wm-grid" data-testid="conquest-grid">
              {map.settlements.map((s) => (
                <button key={s.slot} className={`wm-cell ${s.kind} ${s.is_self ? "self" : ""} ${s.immune ? "immune" : ""} ${s.shielded ? "shielded" : ""} ${march && march.slot === s.slot ? "marching" : ""}`}
                  data-testid={`conquest-cell-${s.slot}`} disabled={busy || s.is_self || s.immune || s.shielded} onClick={() => openGate(s.slot, s.is_self, s.immune, s.shielded)}>
                  <span className="wm-icon">{s.is_self ? <Crown size={15} /> : s.kind === "bot" ? <Bot size={15} /> : <User size={15} />}</span>
                  <span className="wm-name">{s.is_self ? "YOU" : s.name}</span>
                  <span className="wm-def">DEF {s.defense.toLocaleString()}</span>
                  {s.immune && <span className="wm-badge" data-testid={`conquest-immune-${s.slot}`}><ShieldAlert size={9} /> IMMUNE</span>}
                  {s.shielded && <span className="wm-badge shield" data-testid={`conquest-shield-${s.slot}`}><ShieldCheck size={9} /> {Math.ceil(s.shield_remaining / 3600)}h</span>}
                  {march && march.slot === s.slot && <span className="wm-count" data-testid="conquest-countdown">{march.remaining}s</span>}
                  {!s.is_self && !s.immune && !s.shielded && !(march && march.slot === s.slot) && <span className="wm-atk">MARCH</span>}
                </button>
              ))}
            </div>

            {march && <div className="wm-laser" data-testid="conquest-laser" />}

            {board.length > 0 && (
              <div className="wm-board" data-testid="conquest-leaderboard">
                <span className="wm-board-t">Bracket Supremacy</span>
                {board.map((b) => (
                  <div key={b.slot} className={`wm-board-row ${b.is_self ? "me" : ""}`} data-testid={`leaderboard-rank-${b.rank}`}>
                    <span className="r">#{b.rank}</span>
                    <span className="n">{b.is_self ? "YOU" : b.name}</span>
                    <span className="s">PWR {b.power.toLocaleString()} · {b.victories}W</span>
                  </div>
                ))}
              </div>
            )}
            {last && (
              <div className={`wm-result ${last.win ? "win" : "lose"}`} data-testid="conquest-result">
                {last.win ? `Sacked ${last.target_name} — +${last.loot} T · +${last.tokens_awarded} tokens` : `Wiped by ${last.target_name} — 12h Rebuild Shield active`}
              </div>
            )}
          </>
        )}
      </div>

      {gate && (
        <div className="wm-gate-overlay" data-testid="conquest-preflight-gate" onClick={(e) => { if (e.target === e.currentTarget) setGate(null); }}>
          <div className="wm-gate">
            <button className="wm-gate-x" data-testid="gate-cancel" onClick={() => setGate(null)}><X size={16} /></button>

            {gate.loading ? (
              <div className="bg-loading" style={{ padding: "28px 0" }}><Loader2 size={22} className="bg-spin" /><span>Scouting sector…</span></div>
            ) : gate.needSquad ? (
              <div className="wm-gate-need" data-testid="gate-need-squad">
                <Users size={26} style={{ color: "#39ff7a" }} />
                <h3>No squad deployed</h3>
                <p>Deploy up to 5 heroes before you can march on the Conquest map.</p>
                <button className="wm-gate-deploy" data-testid="gate-goto-deploy" onClick={() => { setGate(null); onDeploy && onDeploy(); }}>
                  Go to Squad Deploy
                </button>
              </div>
            ) : gate.data && (
              <>
                <p className="ss-eyebrow" style={{ textAlign: "center" }}>Pre-Flight · March Briefing</p>
                <h3 className="wm-gate-target">vs {gate.data.target.name}</h3>

                <div className="wm-gate-squad" data-testid="gate-squad">
                  {gate.data.heroes.map((h, i) => (
                    <div key={h.card_id} className="wm-gate-hero" data-testid={`gate-hero-${i}`}>
                      {imgUrl(h.image_path)
                        ? <img src={imgUrl(h.image_path)} alt={h.name} className="wm-gate-portrait" />
                        : <span className="wm-gate-letter">{(h.name || "?")[0]}</span>}
                      <span className="wm-gate-hname">{h.name}</span>
                    </div>
                  ))}
                </div>

                <div className="wm-gate-power">
                  <div className="wm-gate-side">
                    <span className="l">Your Power</span>
                    <b data-testid="gate-your-power" style={{ color: "#39ff7a" }}>{gate.data.effective_power.toLocaleString()}</b>
                    {gate.data.matchup_mod !== 0 && (
                      <span className="wm-gate-mod" style={{ color: gate.data.matchup_mod > 0 ? "#39ff7a" : "#ff8a95" }}>
                        base {gate.data.squad_power.toLocaleString()} {gate.data.matchup_mod > 0 ? "+" : ""}{Math.round(gate.data.matchup_mod * 100)}%
                      </span>
                    )}
                  </div>
                  <Swords size={16} className="wm-gate-vs" />
                  <div className="wm-gate-side" style={{ textAlign: "right" }}>
                    <span className="l">Sector Defense</span>
                    <b data-testid="gate-sector-def" style={{ color: "#ff8a95" }}>{gate.data.target.defense.toLocaleString()}</b>
                    {gate.data.target.archetype && <span className="wm-gate-mod">{gate.data.target.archetype} node</span>}
                  </div>
                </div>

                <div className="wm-gate-diff" data-testid="gate-difficulty"
                  style={{ color: DIFF_COLOR[gate.data.difficulty], borderColor: DIFF_COLOR[gate.data.difficulty] }}>
                  {gate.data.difficulty} · {Math.round(gate.data.win_probability * 100)}% win
                </div>

                {gate.data.badges.length > 0 && (
                  <div className="wm-gate-badges">
                    {gate.data.badges.map((b, i) => (
                      <span key={i} className="wm-gate-badge" data-testid={`gate-badge-${i}`}>
                        <Zap size={10} /> {b.label} <b>{b.value}</b>
                      </span>
                    ))}
                  </div>
                )}

                <div className="wm-gate-actions">
                  <button className="wm-gate-cancel" data-testid="gate-cancel-btn" onClick={() => setGate(null)}>Cancel</button>
                  <button className="wm-gate-go" data-testid="gate-confirm-march" onClick={confirmGate}>
                    <Swords size={14} /> March
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
